import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { contractRouter } from '@erp/rpc';
import { orpc } from './client';

const warned = new Set<string>();

/** Walk a dotted path through a plain object tree; undefined when a segment is missing. */
function walk(root: unknown, segments: string[]): unknown {
  let node: unknown = root;
  for (const segment of segments) {
    if (node === null || typeof node !== 'object') return undefined;
    node = (node as Record<string, unknown>)[segment];
  }
  return node;
}

/**
 * `"accountsReceivable.salesOrdersV3.get"` → that procedure's TanStack key
 * prefix (`orpc.accountsReceivable.salesOrdersV3.get.key()`), or undefined
 * when the path names nothing in the contract. The contract is checked first
 * because the `orpc` utils object is a Proxy that answers every path.
 */
export function resolveOrpcKeyPath(path: string): QueryKey | undefined {
  const segments = path.split('.').filter(Boolean);
  if (segments.length === 0) return undefined;
  if (walk(contractRouter, segments) === undefined) return undefined;
  const utils = walk(orpc, segments) as { key?: () => QueryKey } | undefined;
  return typeof utils?.key === 'function' ? utils.key() : undefined;
}

/**
 * Invalidate the queries behind a server tool result's `invalidates`
 * (dotted contract paths, possibly namespaces). Returns the paths it could
 * not resolve; each is warned once so a renamed procedure is visible without
 * spamming the console.
 */
export function invalidateOrpcPaths(
  queryClient: QueryClient,
  paths: readonly string[],
): string[] {
  const unresolved: string[] = [];
  for (const path of paths) {
    const key = resolveOrpcKeyPath(path);
    if (!key) {
      unresolved.push(path);
      if (!warned.has(path)) {
        warned.add(path);
        console.warn(`[nami] unknown invalidation path "${path}"`);
      }
      continue;
    }
    void queryClient.invalidateQueries({ queryKey: key });
  }
  return unresolved;
}

/** The `invalidates` list a tool result carries, if any. */
export function readInvalidates(result: unknown): string[] | undefined {
  if (result === null || typeof result !== 'object') return undefined;
  const value = (result as { invalidates?: unknown }).invalidates;
  return Array.isArray(value) &&
    value.every((item): item is string => typeof item === 'string')
    ? value
    : undefined;
}
